import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle2,
  ArrowRight,
  Phone,
  RefreshCw,
  Users,
  CalendarClock,
  Edit3,
  Bell,
  MessageSquare,
  BarChart3,
  LayoutDashboard,
  Award
} from 'lucide-react';

export const LifecyclePackPage = () => {
  const problemGroups = [
    {
      title: 'Renewal and validity challenges',
      icon: CalendarClock,
      items: [
        { p: 'Missed renewal deadlines', s: 'proactive tracking and advance reminders' },
        { p: 'Expired registrations causing import blocks', s: 'continuous monitoring and timely renewal submissions' },
        { p: 'Changing renewal requirements', s: 'up-to-date knowledge of country-specific rules' }
      ]
    },
    {
      title: 'Variation and change challenges',
      icon: Edit3,
      items: [
        { p: 'Formulation or excipient changes', s: 'variation dossier preparation and submission support' },
        { p: 'New packaging or artwork requirements', s: 'artwork update management and compliance review' },
        { p: 'Manufacturing site changes', s: 'site-transfer documentation and regulatory notification' }
      ]
    },
    {
      title: 'Regulatory update challenges',
      icon: Bell,
      items: [
        { p: 'Country-specific regulatory changes', s: 'regulatory intelligence and impact assessment' },
        { p: 'New labelling or packaging rules', s: 'proactive compliance updates to existing registrations' },
        { p: 'Post-market surveillance requirements', s: 'documentation support for periodic safety reports' }
      ]
    },
    {
      title: 'Query and compliance challenges',
      icon: MessageSquare,
      items: [
        { p: 'Importer or MoH audit queries', s: 'technical response preparation and coordination' },
        { p: 'Post-approval deficiency letters', s: 'expert analysis and resolution documentation' },
        { p: 'Annual compliance requirements', s: 'a compliance calendar and documentation support' }
      ]
    }
  ];

  const deliverables = [
    {
      icon: RefreshCw,
      title: 'Renewals Management',
      lede: 'Renewals never slip. Alerts, evidence, and filing coordinated well before expiry.',
      bullets: [
        'Alerts 6 to 12 months in advance',
        'Document collection',
        'Filing with the local Ministry of Health through your importer'
      ]
    },
    {
      icon: Edit3,
      title: 'Variations & Amendments',
      lede: 'Every post-approval change filed correctly, the first time.',
      bullets: [
        'Artwork updates',
        'Label changes',
        'Formulation, site, and pack-size modifications',
        'New strengths and line extensions'
      ]
    },
    {
      icon: MessageSquare,
      title: 'Deficiency & Query Handling',
      lede: 'Post-approval queries resolved with expert responses and evidence.',
      bullets: [
        'Drafting responses with your importer',
        'Evidence collection',
        'Regulatory justification letters'
      ]
    },
    {
      icon: BarChart3,
      title: 'Annual Compliance Review',
      lede: 'A yearly sweep across global guidelines and market rules.',
      bullets: [
        'Check for global guideline changes (WHO, GCC, ASEAN, and more)',
        'Stability protocol updates',
        'Market intelligence on new rules'
      ]
    },
    {
      icon: LayoutDashboard,
      title: 'Full Lifecycle Tracker',
      lede: 'One dashboard for every registration, every market.',
      bullets: ['A dashboard summarizing registration status across all your countries']
    }
  ];

  const processSteps = [
    'Share your approved SKUs and markets',
    'We map renewal cycles and variation rules',
    'Continuous compliance management',
    'Annual report and improvement plan'
  ];

  const idealFor = [
    'Exporters with 5 to 50 active registrations',
    'Busy RA teams needing external support',
    'Manufacturers expanding into multiple new markets',
    'Brands with frequent SKU changes'
  ];

  const faqs = [
    {
      q: 'What is post-approval regulatory support?',
      a: "Post-approval regulatory support keeps a product's registration active and compliant after it has been approved. It covers renewals, variations, post-approval changes, deficiency and query handling, and annual compliance review, so the registration you have won stays valid and you never lose market access to a missed deadline."
    },
    {
      q: 'How do you manage registration renewals?',
      a: "We track every registration's expiry, alert you 6 to 12 months in advance, collect the required documents, and file the renewal with the local Ministry of Health through your importer. Continuous monitoring means an expired registration never blocks your imports."
    },
    {
      q: 'What counts as a variation, and do you handle it?',
      a: 'A variation is any change to an approved product, such as a formulation or excipient change, new packaging or artwork, a manufacturing site change, a new strength, or a line extension. We prepare and submit the variation dossier for each of these, with the supporting documentation and regulatory notification each market requires.'
    },
    {
      q: 'Can you respond to regulator queries after approval?',
      a: 'Yes. We handle post-approval deficiency letters and importer or MoH audit queries with expert analysis, evidence collection, and regulatory justification letters, drafted in coordination with your importer to resolve the query quickly.'
    },
    {
      q: 'How do you keep track of registrations across multiple countries?',
      a: 'The pack includes a full lifecycle tracker, a dashboard summarizing registration status across every country, plus an annual compliance review that checks for global guideline changes, stability protocol updates, and new market rules. This suits exporters managing anywhere from 5 to 50 active registrations.'
    },
    {
      q: 'Who is the Lifecycle Support Pack for?',
      a: 'It is built for exporters with multiple active registrations, busy regulatory affairs teams needing external support, manufacturers expanding into new markets, and brands with frequent SKU changes, anyone for whom losing a registration to an administrative oversight would be costly.'
    }
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Lifecycle Support Pack',
    'alternateName': 'Pharma Post-Approval Renewal, Variation & Lifecycle Support',
    'serviceType': 'Post-Approval Regulatory Support and Lifecycle Management',
    'description':
      'Post-approval regulatory support for pharma and nutra exporters covering renewals, variations, deficiency handling, annual compliance review and full lifecycle tracking across active registrations in Africa, GCC, Asia and Latin America.',
    'provider': { '@type': 'Organization', 'name': 'TheApostrophe', 'url': 'https://www.theapostrophe.com' },
    'areaServed': ['Africa', 'GCC', 'Asia', 'Latin America', 'ASEAN'],
    'url': 'https://www.theapostrophe.com/success-packs/lifecycle-pack',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'url': 'https://www.theapostrophe.com/success-packs/lifecycle-pack'
    }
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How the Lifecycle Support Pack works',
    'description': 'A four-step process to keep every product registration active, updated and audit-ready year after year.',
    'step': processSteps.map((s, i) => ({
      '@type': 'HowToStep',
      'position': i + 1,
      'name': `Step ${i + 1}`,
      'text': s
    }))
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.theapostrophe.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Success Packs', 'item': 'https://www.theapostrophe.com/success-packs' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Lifecycle Support Pack', 'item': 'https://www.theapostrophe.com/success-packs/lifecycle-pack' }
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Pharma Post-Approval Renewal, Variation & Lifecycle Support</title>
        <meta
          name="description"
          content="Post-approval regulatory support for pharma exporters: renewals, variations, deficiency handling and lifecycle management across your active registrations."
        />
        <meta
          name="keywords"
          content="post-approval regulatory support, pharma lifecycle management, pharma registration renewal, variation dossier submission, post-market regulatory support, deficiency query handling, post-approval change management, registration maintenance, regulatory tracker dashboard"
        />
        <link rel="canonical" href="https://www.theapostrophe.com/success-packs/lifecycle-pack" />

        <meta property="og:title" content="Pharma Post-Approval Renewal, Variation & Lifecycle Support" />
        <meta property="og:description" content="Renewals, variations, deficiency handling and lifecycle management for pharma and nutra exports across Africa, GCC, Asia and LATAM." />
        <meta property="og:url" content="https://www.theapostrophe.com/success-packs/lifecycle-pack" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pharma Post-Approval Renewal, Variation & Lifecycle Support" />
        <meta name="twitter:description" content="Keep every registration current, compliant and audit-ready with continuous post-approval regulatory support." />

        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2" style={{ backgroundColor: '#708080', color: 'white' }} data-testid="lifecycle-pack-badge">
            Success Pack 5
          </Badge>
          <div className="w-24 h-24 rounded-2xl bg-white/60 flex items-center justify-center mb-6 mx-auto shadow-md">
            <RefreshCw className="h-12 w-12" style={{ color: '#708080' }} aria-hidden="true" />
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            style={{ color: '#303040' }}
            data-testid="lifecycle-pack-h1"
          >
            Post-Approval Support: Renewals, Variations and Lifecycle Management
          </h1>
          <p className="text-xl md:text-2xl font-bold mb-8" style={{ color: '#708080' }}>
            After approval, every product needs compliance upkeep. We keep your registrations active, updated, and fully compliant.
          </p>
          <p className="text-base md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed" style={{ color: '#606070' }}>
            Getting approved is not the finish line. Registrations expire, formulations change, artwork gets updated, and regulators send queries long after launch. Miss a renewal deadline and you lose market access overnight. The Lifecycle Support Pack keeps your regulatory presence active year-round, handling renewals, variations, post-approval changes, and regulator queries, so you never lose a registration to an administrative oversight.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#708080', color: 'white' }}
                data-testid="lifecycle-manage-btn"
              >
                Manage My Lifecycle
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#708080', color: '#708080' }}
                data-testid="lifecycle-review-btn"
              >
                Book a Compliance Review
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What It Solves — grouped */}
      <section className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: '#303040' }}>
            What It Solves
          </h2>
          <p className="text-base md:text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Once a product is approved, the regulatory journey does not end, it evolves. Different challenges need different support.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {problemGroups.map((group, gi) => (
              <div key={gi} className="rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" style={{ backgroundColor: '#F8F4EE' }}>
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#708080' }}
                  >
                    <group.icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: '#303040' }}>
                    {group.title}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {group.items.map((item, ii) => (
                    <li key={ii} className="text-sm md:text-base leading-relaxed" style={{ color: '#606070' }}>
                      <span className="font-semibold" style={{ color: '#505060' }}>
                        {item.p}
                      </span>
                      <span className="mx-2" style={{ color: '#708080' }} aria-hidden="true">→</span>
                      <span>solved with {item.s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get - Deliverables */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            What You Get
          </h2>
          <p className="text-base md:text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Five deliverables that keep every registration current, compliant, and audit-ready.
          </p>
          <div className="space-y-10">
            {deliverables.map((d, i) => (
              <article
                key={i}
                className="rounded-3xl p-8 md:p-12 shadow-md hover:shadow-xl transition-shadow"
                style={{ backgroundColor: 'white' }}
              >
                <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
                  <div className="flex md:flex-col items-start gap-4 md:gap-3">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#708080' }}
                    >
                      <d.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <div className="text-4xl font-bold" style={{ color: '#708080', opacity: 0.5 }}>
                      0{i + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                      {d.title}
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#606070' }}>
                      {d.lede}
                    </p>
                    <div className="space-y-2">
                      {d.bullets.map((b, bi) => (
                        <div key={bi} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#708080' }} aria-hidden="true" />
                          <span className="text-base" style={{ color: '#505060' }}>
                            {b}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Process
          </h2>
          <ol className="space-y-6">
            {processSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg"
                  style={{ backgroundColor: '#708080' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <p className="text-base md:text-lg leading-relaxed pt-2" style={{ color: '#505060' }}>
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Users className="h-8 w-8" style={{ color: '#708080' }} aria-hidden="true" />
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#303040' }}>
              Ideal For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {idealFor.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-6 rounded-xl" style={{ backgroundColor: '#F8F4EE' }}>
                <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: '#708080' }} aria-hidden="true" />
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 md:p-12 rounded-3xl" style={{ backgroundColor: 'rgba(112, 128, 128, 0.14)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Award className="h-7 w-7" style={{ color: '#708080' }} aria-hidden="true" />
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#708080' }}>
                Why This Matters
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#505060' }}>
              Post-approval compliance is where most companies slip. This pack ensures zero loss of registration, no missed deadlines, and smooth regulator communication year-round. The registrations you worked months to win are only as secure as the upkeep behind them.
            </p>
          </div>
        </div>
      </section>

      {/* Retainer CTA Block */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-8 md:p-12 shadow-xl" style={{ backgroundColor: 'white' }}>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#A060B0' }}
              >
                <Users className="h-10 w-10 text-white" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#303040' }}>
                  Want your entire regulatory workflow handled continuously?
                </h2>
                <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#708080' }}>
                  If you are managing renewals and variations across several markets, a retainer keeps it all under one expert. The <strong>Apostrophe Retainer</strong> gives you one dedicated regulatory affairs professional managing every stage on an ongoing basis, so you never have to hire, train, or run an in-house regulatory team.
                </p>
                <Link to="/retainer">
                  <Button
                    size="lg"
                    className="px-6 py-5 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    data-testid="lifecycle-retainer-btn"
                  >
                    Explore the Retainer Plan
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.08)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#303040' }}>
            Ready for Hassle-Free Lifecycle Management?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#606070' }}>
            Share your approved SKUs and markets, and we will keep every registration current, compliant, and audit-ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#708080', color: 'white' }}
                data-testid="lifecycle-cta-start-btn"
              >
                Start Lifecycle Management
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#708080', color: '#708080' }}
                data-testid="lifecycle-cta-meeting-btn"
              >
                Schedule a Meeting
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ - AEO */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center text-base md:text-lg mb-16" style={{ color: '#708080' }}>
            Straight answers about renewals, variations, deficiencies and post-approval compliance.
          </p>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl p-6 md:p-8 transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
                open={i === 0}
              >
                <summary
                  className="cursor-pointer list-none flex items-start justify-between gap-4"
                  data-testid={`lifecycle-faq-${i}`}
                >
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: '#303040' }}>
                    {f.q}
                  </h3>
                  <span
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform group-open:rotate-45"
                    style={{ backgroundColor: '#708080', color: 'white' }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="text-base md:text-lg leading-relaxed mt-4" style={{ color: '#606070' }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
